import React, { createContext, useState } from 'react'

/*
    Although the SelectionContext can be used for any table to manage the
    selected items, it is specifically tuned for managing paged tables where
    not all data is loaded from the server and additional pages are fetched
    when needed.

    Select a parent component above the table in the heirarchy and 
    introduce a <SelectionContext> that will wrap the table and all
    child components.

    At each level below (the table, its header or row components) will then
    have access to the values of the SelectionContext to avoid prop-drilling.

*/

const stubError = (...args) => {
  console.error(['Stubs should not be used', ...args])
}

// Note: the stub implementations supplied to createContext will never be
// used in practice.  They are supplied to any consumer that uses a
// SelectionContext without being wrapped in a SelectionContextProvider.
// They are defined here to establish the type definitions for the IDE and
// typescript, but are not otherwise used.
const SelectionContext = createContext({
  allCheckboxIsChecked: false,
  allLoadedRecordKeys: new Set<string>(),
  disableCheckBox: false,
  forceRerender: (render: boolean) => {
    stubError(render)
  },
  handleAllCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => {
    stubError(e)
  },
  handleSingleCheckboxChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string
  ) => {
    stubError(e, key)
  },
  keyIsSelected: (key: string) => {
    stubError(key)
  },
  mergeWithAllLoadedRecordKeys: (keys: string[]) => {
    stubError(keys)
  },
  reset: () => {
    stubError()
  },
  selectOneRecordKey: (key: string) => {
    stubError(key)
  },
  selectedRecordKeys: new Set<string>(),
  unloadedRecordsAreSelected: false
})

const SelectionContextProvider: React.FC = ({ children }) => {
  const [disableCheckBox, setDisableCheckBox] = useState<boolean>(false)
  const [allLoadedRecordKeys, setAllLoadedRecordKeys] = useState(
    new Set<string>()
  )
  const [unloadedRecordsAreSelected, setUnloadedRecordsAreSelected] =
    useState(false)
  const [selectedRecordKeys, setSelectedRecordKeys] = useState(
    new Set<string>()
  )
  const [allCheckboxIsChecked, setAllCheckboxIsChecked] = useState(false)

  const reset = () => {
    setAllCheckboxIsChecked(false)
    setUnloadedRecordsAreSelected(false)
    setSelectedRecordKeys(new Set<string>())
    setAllLoadedRecordKeys(new Set<string>())
  }

  const handleAllCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked

    setAllCheckboxIsChecked(isChecked)
    setUnloadedRecordsAreSelected(isChecked)

    if (isChecked) {
      setSelectedRecordKeys(new Set(allLoadedRecordKeys))
    } else {
      setSelectedRecordKeys(new Set<string>())
    }
  }

  const handleSingleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string
  ) => {
    const isChecked = e.target.checked

    if (isChecked) selectOneRecordKey(key)
    else deselectOneRecordKey(key)
  }

  const selectOneRecordKey = (key: string) => {
    setSelectedRecordKeys((currentKeys) => {
      const newKeys = new Set(currentKeys)
      newKeys.add(key)

      // If the user has manually checked the last unchecked record in the list,
      // we update the All Checkbox
      if (
        allLoadedRecordKeys.size > 0 &&
        newKeys.size === allLoadedRecordKeys.size &&
        unloadedRecordsAreSelected
      ) {
        setAllCheckboxIsChecked(true)
      }
      return newKeys
    })
  }

  const deselectOneRecordKey = (key: string) => {
    // When the user deselects a record, the Select All checkbox for the
    // table should be deselected to indicate not all records are selected.
    // However, we do not clear the "unloadedRecordsAreSelected" flag.
    // That will only be cleared if they toggle the Select All checkbox
    // directly from checked to unchecked.
    setAllCheckboxIsChecked(false)

    setSelectedRecordKeys((currentKeys) => {
      const newKeys = new Set(currentKeys)
      newKeys.delete(key)
      return newKeys
    })
  }

  const mergeWithAllLoadedRecordKeys = (keys: string[]) => {
    const newKeys = keys.filter(k => !allLoadedRecordKeys.has(k))

    if (newKeys.length <= 0) return

    setAllLoadedRecordKeys((currKeys) => {
      unloadedRecordsAreSelected &&
        setSelectedRecordKeys(new Set([...selectedRecordKeys, ...newKeys]))

      return new Set([...currKeys, ...newKeys])
    })
  }

  const keyIsSelected = (key: string) => {
    return selectedRecordKeys.has(key)
  }
  const forceRerender = (rerender: boolean) => {
    setDisableCheckBox(!rerender)
  }

  return (
    <SelectionContext.Provider
      value={{
        // bind this state to the "checked" attribute of the Select All
        // checkbox (typically in the upper-left of the table)
        allCheckboxIsChecked,

        // child components of this context can get a Set<string> that
        // contains the keys of all loaded records.  (See selectedRecordKeys
        // for the set that has only those keys that are selected.)
        // Note that the SelectionContext only knows about keys that
        // have been merged via mergeWithAllLoadedRecordKeys().
        // Unloaded pages yet to be fetched from the server won't be present
        allLoadedRecordKeys,

        // The change causing component to render
        disableCheckBox,
        
        // This is simply to cause component to re-render when called
        forceRerender,

        // connect this to the "onChange" event of the Select All checkbox
        handleAllCheckboxChange,

        // connect this to the "onChange" event of the checkboxes for each
        // row.  Pass a key (a string that uniquely identifies the row) and
        // the change event
        handleSingleCheckboxChange,

        // consumers of this context can call keyIsSelected(key) to see if a
        // given key is selected
        keyIsSelected,

        // In a paged table, as each additional page of data is loaded, pass
        // the keys for these rows to the mergeWithAllLoadedRecordKeys.
        // When used in an unpaged table, this can be called with the full
        // list of keys for all records
        mergeWithAllLoadedRecordKeys,

        // Child components can call reset to set the selections back to initial
        // state.  (No items selected, Select All box unselected, etd)
        reset,

        // call this method to select an item by key -- since the context will
        // manage selected records through  handleAllCheckboxChange() and
        // handleSingleCheckboxChange() this won't normally be needed.
        selectOneRecordKey,

        // child components can get the selected record keys using this
        // function.  Note: the SelectionContext can only track the selected
        // keys of records that have been loaded.
        // Users of this function must also inspect the value of
        // "unloadedRecordsAreSelected" and if true, compare the total
        // records they expect from the server versus the length of the
        // allLoadedRecordKeys value to see if a full fetch from the server
        // is needed
        selectedRecordKeys,

        // when true, the user has sometime in the past checked the
        // Select All checkbox.  Even if they have later unchecked one item
        // in the list (therfore changing the Select All checkbox back to
        // unchecked) this setting is preserved.  It is only cleared back to
        // false on reset() or if the user again checks Select All, then
        // unchecks it
        unloadedRecordsAreSelected,
      }}
    >
      {children}
    </SelectionContext.Provider>
  )
}

export { SelectionContext, SelectionContextProvider }
